import { useEffect } from "react";
import { useLocation, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import { useOrganizationParentGetOrganizationSettings } from "@/orval/organization-parent";
import {
  getOrganizationSemesterDropdownGetOneQueryKey,
  useOrganizationSemesterDropdownGetOne,
} from "@/orval/organization-semester-dropdown";

export const enrollmentSearchSchema = z.object({
  semesterId: z
    .union([z.number(), z.string().regex(/^\d+$/).transform(Number)])
    .pipe(z.number().int().positive().max(Number.MAX_SAFE_INTEGER))
    .optional()
    .catch(undefined),
});

// Only an organization-scoped option may unlock enrollment/agreement requests.
export function useEnrollmentSemester(organizationId: number, selectedSemesterId?: number) {
  const navigate = useNavigate();
  const locationSearch = useLocation({
    select: (location) => ({
      hasSemester: new URLSearchParams(location.searchStr).has("semesterId"),
      semesterId: enrollmentSearchSchema.parse(location.search).semesterId,
    }),
  });
  const settingsQuery = useOrganizationParentGetOrganizationSettings(organizationId);
  const settings = settingsQuery.data;
  const candidateId = selectedSemesterId ?? settings?.nextSemesterId ?? settings?.activeSemesterId ?? undefined;
  const option = useOrganizationSemesterDropdownGetOne(organizationId, candidateId!, {
    query: {
      enabled: !!candidateId,
      retry: false,
      refetchOnMount: "always",
      staleTime: 0,
      // Keep validation independent of the combobox's option-loading lifecycle.
      queryKey: [
        ...getOrganizationSemesterDropdownGetOneQueryKey(organizationId, candidateId!),
        "enrollment-validation",
      ],
    },
  });
  const isRejected = option.isError || (option.isSuccess && option.data?.value !== candidateId);
  const semesterId = option.isSuccess && option.isFetchedAfterMount && !isRejected ? candidateId : undefined;
  // Location may update before this route's match commits its new search state.
  const invalidSearch =
    locationSearch.hasSemester &&
    (locationSearch.semesterId === undefined || (locationSearch.semesterId === selectedSemesterId && isRejected));

  useEffect(() => {
    if (invalidSearch) {
      void navigate({ to: ".", search: (previous) => ({ ...previous, semesterId: undefined }), replace: true });
    }
  }, [invalidSearch, navigate]);

  return {
    semesterId,
    selectedSemesterId: semesterId === selectedSemesterId ? selectedSemesterId : undefined,
    isPending:
      invalidSearch ||
      (!candidateId && settingsQuery.isPending) ||
      (!!candidateId && !option.isFetchedAfterMount && !option.isError),
  };
}
